import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import LazyImage from './LazyImage';

const PhotoMosaic = ({ photos, onPhotoClick, onDeletePhoto, onSetCover, currentCover, selectedPhotos = new Set(), onToggleSelection }) => {
  const { isAuthorized } = useAuth();
  const selectionMode = selectedPhotos.size > 0;
  if (photos.length === 0) {
    return (
      <div className="text-center py-12 text-text-dark">
        <p className="text-lg m-0 font-sans">No photos in this album yet. Upload some photos to get started!</p>
      </div>
    );
  }

  return (
    <div className="columns-2 md:columns-3 lg:columns-4 xl:columns-5 2xl:columns-6 gap-2 sm:gap-4 py-2 sm:py-4">
      {photos.map((photo) => {
        const isCover = currentCover?.name === photo.name;
        const isSelected = selectedPhotos.has(photo.id);
        return (
          <div
            key={photo.id}
            className={`relative inline-block w-full mb-2 sm:mb-4 break-inside-avoid overflow-hidden rounded-lg shadow-md transition-all duration-200 group ${
              isSelected
                ? 'border-4 border-blue-500 shadow-blue-500/50'
                : isCover
                ? 'border-3 border-accent shadow-accent/50 hover:-translate-y-0.5 hover:shadow-lg'
                : 'border border-gray-200 hover:-translate-y-0.5 hover:shadow-lg'
            } ${selectionMode ? 'cursor-pointer' : ''}`}
            onClick={selectionMode ? () => onToggleSelection(photo.id) : undefined}
          >
            {photo.isVideo && !photo.thumbnailUrl ? (
              // Placeholder for videos without thumbnails yet
              <div
                className="w-full aspect-video bg-gray-800 flex items-center justify-center cursor-pointer transition-transform duration-200 hover:scale-105"
                onClick={!selectionMode ? () => onPhotoClick(photo, photos) : undefined}
              >
                <span className="material-icons text-gray-600 text-6xl">videocam</span>
              </div>
            ) : (
              // Image or video with thumbnail
              <LazyImage
                src={photo.url}
                thumbnailUrl={photo.thumbnailUrl}
                alt={photo.name}
                onClick={!selectionMode ? () => onPhotoClick(photo, photos) : undefined}
                className="w-full h-auto block cursor-pointer transition-transform duration-200 hover:scale-105"
              />
            )}

            {/* Selection checkbox - always visible on hover or when selected */}
            <div className={`absolute top-2 left-2 ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity duration-200`}>
              <button
                className={`w-7 h-7 rounded-full cursor-pointer flex items-center justify-center transition-all duration-200 shadow-lg ${
                  isSelected
                    ? 'bg-blue-600 border-2 border-blue-600'
                    : 'bg-black/20 backdrop-blur-sm border-2 border-white'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleSelection(photo.id);
                }}
                title={isSelected ? "Deselect photo" : "Select photo"}
              >
                {isSelected && (
                  <span className="material-icons text-white text-sm">check</span>
                )}
              </button>
            </div>

            {/* Blue overlay for selected photos */}
            {isSelected && (
              <div className="absolute inset-0 bg-blue-500/20 pointer-events-none" />
            )}

            {photo.isVideo && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-16 h-16 bg-black/60 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <span className="material-icons text-white text-4xl">play_arrow</span>
                </div>
              </div>
            )}

            {!selectionMode && isCover && (
              <div className="absolute top-12 left-2 bg-accent/95 text-text-light px-2 py-1 rounded-xl flex items-center gap-1 text-xs font-semibold uppercase tracking-wide">
                <span className="material-icons text-base">star</span>
                <span>Cover</span>
              </div>
            )}

            {!selectionMode && (
              <div className="absolute top-2 right-2 flex gap-2 opacity-0 transition-opacity duration-200 group-hover:opacity-100 hover:opacity-100">
              <button
                className="w-8 h-8 border-none rounded-full cursor-pointer flex items-center justify-center text-text-light transition-all duration-200 bg-accent/90 hover:bg-accent hover:scale-110"
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    // Fetch the image as a blob to bypass CORS restrictions
                    const response = await fetch(photo.url);
                    const blob = await response.blob();
                    const blobUrl = window.URL.createObjectURL(blob);

                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = photo.name || 'photo';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    // Clean up the blob URL
                    window.URL.revokeObjectURL(blobUrl);
                  } catch (error) {
                    console.error('Error downloading photo:', error);
                    // Fallback to opening in new tab
                    window.open(photo.url, '_blank');
                  }
                }}
                title="Download photo"
              >
                <span className="material-icons text-base">download</span>
              </button>
              
              {isAuthorized && !isCover && !photo.isVideo && (
                <button
                  className="w-8 h-8 border-none rounded-full cursor-pointer flex items-center justify-center text-text-light transition-all duration-200 bg-accent/90 hover:bg-accent hover:scale-110"
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('PhotoMosaic: Setting cover for photo:', photo);
                    onSetCover(photo);
                  }}
                  title="Set as album cover"
                >
                  <span className="material-icons text-base">star_border</span>
                </button>
              )}
              
              {isAuthorized && (
                <button
                  className="w-8 h-8 border-none rounded-full cursor-pointer flex items-center justify-center text-white transition-all duration-200 bg-red-500/90 hover:bg-red-500 hover:scale-110"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm(`Delete photo "${photo.name}"?`)) {
                      onDeletePhoto(photo.name);
                    }
                  }}
                  title="Delete photo"
                >
                  <span className="material-icons text-base">close</span>
                </button>
              )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PhotoMosaic;